import api from '../utils/api';

/**
 * @typedef {Object} Blog
 * @property {string} _id
 * @property {string} title
 * @property {string} slug
 * @property {string} content
 * @property {string} author
 * @property {string} thumbnail
 * @property {string[]} tags
 * @property {string} courseId
 * @property {number} readTime
 * @property {number} likes
 * @property {string} createdAt
 * @property {string} updatedAt
 */

/**
 * @typedef {Object} CreateBlogData
 * @property {string} title
 * @property {string} content
 * @property {string} [thumbnail]
 * @property {string[]} [tags]
 * @property {string} [courseId]
 */

export const blogService = {
  // ==================
  // PUBLIC ROUTES
  // ==================

  /**
   * Get all blogs
   * @returns {Promise<{status: string, count: number, blogs: Blog[]}>}
   */
  getAllBlogs: async () => {
    const response = await api.get('/api/blogs');
    return response;
  },

  /**
   * Get all blogs (Admin Only)
   * @param {Object} params - Search, status, type, etc.
   * @returns {Promise<{status: string, count: number, blogs: Blog[], pagination: Object}>}
   */
  getAllBlogsAdmin: async (params) => {
    const response = await api.get('/api/blogs/admin/all', { params });
    return response;
  },

  /**
   * Get blog by slug
   * @param {string} slug 
   * @returns {Promise<{status: string, blog: Blog}>}
   */
  getBlogBySlug: async (slug) => {
    const response = await api.get(`/api/blogs/slug/${slug}`);
    return response;
  },

  /**
   * Get blogs by course
   * @param {string} courseId 
   * @returns {Promise<{status: string, count: number, blogs: Blog[]}>}
   */
  getBlogsByCourse: async (courseId) => {
    const response = await api.get(`/api/blogs/course/${courseId}`);
    return response;
  },

  /**
   * Get blog by ID
   * @param {string} id 
   * @returns {Promise<{status: string, blog: Blog}>}
   */
  getBlogById: async (id) => {
    const response = await api.get(`/api/blogs/${id}`);
    return response;
  },

  // ==================
  // PROTECTED ROUTES
  // ==================

  /**
   * Like blog
   * @param {string} id 
   * @returns {Promise<{status: string, blog: Blog}>}
   */
  likeBlog: async (id) => {
    const response = await api.post(`/api/blogs/${id}/like`);
    return response;
  },

  /**
   * Get my blogs (Instructor/Admin)
   * @returns {Promise<{status: string, count: number, blogs: Blog[]}>}
   */
  getMyBlogs: async () => {
    const response = await api.get('/api/blogs/me/blogs');
    return response;
  },

  /**
   * Create blog (Instructor/Admin)
   * @param {CreateBlogData} blogData 
   * @returns {Promise<{status: string, blog: Blog}>}
   */
  createBlog: async (blogData) => {
    const response = await api.post('/api/blogs', blogData);
    return response;
  },

  /**
   * Update blog (Instructor/Admin)
   * @param {string} id 
   * @param {Object} updates 
   * @returns {Promise<{status: string, blog: Blog}>}
   */
  updateBlog: async (id, updates) => {
    const response = await api.put(`/api/blogs/${id}`, updates);
    return response;
  },

  /**
   * Delete blog (Instructor/Admin)
   * @param {string} id 
   * @returns {Promise<{status: string, message: string}>}
   */
  deleteBlog: async (id) => {
    const response = await api.delete(`/api/blogs/${id}`);
    return response;
  },
};
